import React, { useRef, useCallback, useEffect } from 'react'
import Moveable from 'react-moveable'

import { IMediaItem } from 'types/media'
import { dispatch } from 'store'
import { editorThunks } from 'store/features/editor/thunks'
import editorSlice from 'store/features/editor/slice'

import * as Styled from './styled'
import { useMovableActions } from '../MovableItemsLayer/hooks/use-movable-actions'
import { useLiveSizes } from '../MovableItemsLayer/hooks/use-live-sizes'
import {
  useDirections,
  MIN_RESIZE_SIZE
} from '../MovableItemsLayer/hooks/use-directions'
import { useSnapGuidelines } from '../MovableItemsLayer/hooks/use-snap-guidelines'
import { MAX_FONT_SIZE } from 'consts/text'
import {
  CANVAS_ITEM_FRAME_CLASS_NAME,
  MOBILE_SIZE_BREAKPOINT,
  PROJECT_MEDIA_TYPES
} from 'consts'

import {
  buildFrameElementToIdMap,
  CLICK_MOVE_THRESHOLD_SQ,
  findTopmostCanvasItemIdAtPoint,
  frameDisplayAreaSq
} from './utils'
import { useResponsive } from 'hooks/useResponsive'

interface ICanvasItemFrameProps {
  item: IMediaItem
  mediaItemsById: Map<string, IMediaItem>
  isVisible: boolean
  canvasScale: number
  isGenerating: boolean
  isActive: boolean
  isMovableVisible: boolean
  /** Moveable handles + active outline; hidden while timeline plays (matches media toolbar). */
  isSelectionChromeVisible: boolean
  targetRefsMap: React.MutableRefObject<Map<string, HTMLDivElement>>
  canvasWidth: number
  canvasHeight: number
  onOpenZOrderMenu: (args: { itemId: string; x: number; y: number }) => void
  activeTimelineItemId: string | null
  activeMoveableRef: React.MutableRefObject<Moveable | null>
  /** Pauses timeline if currently playing. Called before any canvas-driven selection. */
  pauseIfPlaying: () => void
}

const CanvasItemFrame: React.FC<ICanvasItemFrameProps> = ({
  item,
  mediaItemsById,
  canvasScale,
  isActive,
  isMovableVisible,
  isSelectionChromeVisible,
  targetRefsMap,
  canvasWidth,
  canvasHeight,
  onOpenZOrderMenu,
  activeTimelineItemId,
  activeMoveableRef,
  pauseIfPlaying
}) => {
  const frameRef = useRef<HTMLDivElement>(null)
  const moveableRef = useRef<Moveable>(null)
  const isMobile = useResponsive(MOBILE_SIZE_BREAKPOINT)
  const selectGestureRef = useRef({
    exceeded: false,
    startX: 0,
    startY: 0
  })
  const selectGestureCleanupRef = useRef<(() => void) | null>(null)
  const routedDragToActiveRef = useRef(false)
  /** True if pointerdown hit stack included the currently selected item's frame. */
  const pointerDownHitIncludesActiveFrameRef = useRef(false)

  const assignMoveableRef = useCallback(
    (instance: Moveable | null) => {
      moveableRef.current = instance
      if (!instance) {
        activeMoveableRef.current = null
        return
      }
      if (isSelectionChromeVisible) {
        activeMoveableRef.current = instance
      }
    },
    [activeMoveableRef, isSelectionChromeVisible]
  )

  const { liveSize, isResizingRef } = useLiveSizes(item, moveableRef)
  const displayWidth = liveSize?.width ?? item.width
  const displayHeight = liveSize?.height ?? item.height

  const { getRenderDirections, directionVectorToKey, setActiveResizeDir } =
    useDirections(displayHeight, isActive)

  const snapProps = useSnapGuidelines({
    itemId: item.id,
    targetRef: frameRef,
    targetRefsMap,
    canvasWidth,
    canvasHeight,
    enabled: isSelectionChromeVisible
  })

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (!isActive) {
        return
      }
      e.preventDefault()
      e.stopPropagation()
      onOpenZOrderMenu({ itemId: item.id, x: e.clientX, y: e.clientY })
    },
    [isActive, item.id, onOpenZOrderMenu]
  )

  const markSelectGestureExceeded = useCallback(() => {
    selectGestureRef.current.exceeded = true
  }, [])

  const startSelectGestureTracking = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) {
        return
      }
      pauseIfPlaying()
      selectGestureCleanupRef.current?.()
      routedDragToActiveRef.current = false

      const hitStack = document.elementsFromPoint(e.clientX, e.clientY)
      const activeFrameEl = activeTimelineItemId
        ? (targetRefsMap.current.get(activeTimelineItemId) ?? null)
        : null
      pointerDownHitIncludesActiveFrameRef.current = !!(
        activeFrameEl && hitStack.includes(activeFrameEl)
      )

      const frameElementToId = buildFrameElementToIdMap(targetRefsMap.current)

      selectGestureRef.current = {
        exceeded: false,
        startX: e.clientX,
        startY: e.clientY
      }

      const selectItemAndScheduleDrag = (
        targetItem: IMediaItem,
        pointerEvent: PointerEvent
      ) => {
        routedDragToActiveRef.current = true
        pauseIfPlaying()
        dispatch(
          editorThunks.selectSingleTimelineItem({
            id: targetItem.id,
            type: targetItem.type
          })
        )
        const eventForDrag = pointerEvent
        requestAnimationFrame(() => {
          const movable = activeMoveableRef.current
          const dragTarget = targetRefsMap.current.get(targetItem.id)
          if (movable && dragTarget) {
            movable.dragStart(eventForDrag, dragTarget)
          }
        })
      }

      const startDraggingCurrentSelection = (
        moveable: Moveable,
        activeElement: HTMLElement,
        pointerEvent: PointerEvent
      ) => {
        routedDragToActiveRef.current = true
        moveable.dragStart(pointerEvent, activeElement)
      }

      const onMove = (mouseEvent: PointerEvent) => {
        const g = selectGestureRef.current
        const dx = mouseEvent.clientX - g.startX
        const dy = mouseEvent.clientY - g.startY
        const wasExceeded = g.exceeded
        if (dx * dx + dy * dy > CLICK_MOVE_THRESHOLD_SQ) {
          g.exceeded = true
        }

        if (
          wasExceeded ||
          !g.exceeded ||
          isActive ||
          routedDragToActiveRef.current
        ) {
          return
        }

        const moveable = activeMoveableRef.current
        const activeElement = activeTimelineItemId
          ? targetRefsMap.current.get(activeTimelineItemId)
          : null

        const pressedOnActiveFrame =
          pointerDownHitIncludesActiveFrameRef.current &&
          activeTimelineItemId !== null &&
          activeTimelineItemId !== item.id

        if (pressedOnActiveFrame) {
          const topId = findTopmostCanvasItemIdAtPoint(
            mouseEvent.clientX,
            mouseEvent.clientY,
            frameElementToId
          )
          const topMedia = topId ? mediaItemsById.get(topId) : undefined
          const selectedMedia = mediaItemsById.get(activeTimelineItemId)

          const topLayerIsSmallerOverlay =
            topMedia !== undefined &&
            selectedMedia !== undefined &&
            frameDisplayAreaSq(topMedia) < frameDisplayAreaSq(selectedMedia)

          if (topLayerIsSmallerOverlay) {
            selectItemAndScheduleDrag(topMedia, mouseEvent)
          } else if (moveable && activeElement) {
            startDraggingCurrentSelection(moveable, activeElement, mouseEvent)
          }
          return
        }

        const pressedOutsideActiveFrame =
          !pointerDownHitIncludesActiveFrameRef.current &&
          item.id !== activeTimelineItemId

        if (pressedOutsideActiveFrame) {
          selectItemAndScheduleDrag(item, mouseEvent)
        }
      }

      const onEnd = () => {
        routedDragToActiveRef.current = false
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onEnd)
        window.removeEventListener('pointercancel', onEnd)
        selectGestureCleanupRef.current = null
      }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onEnd)
      window.addEventListener('pointercancel', onEnd)
      selectGestureCleanupRef.current = () => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onEnd)
        window.removeEventListener('pointercancel', onEnd)
      }
    },
    [
      activeMoveableRef,
      activeTimelineItemId,
      isActive,
      item,
      mediaItemsById,
      targetRefsMap,
      pauseIfPlaying
    ]
  )

  const maybeHandleClick = useCallback(() => {
    // TODO: remove when canvas text editing is enabled.
    // Bump on every click of a text frame, even when the moveable has
    // marked the gesture as exceeded (which happens on every pointerdown
    // for active items via Moveable's onDragStart).
    if (item.type === PROJECT_MEDIA_TYPES.text && !isMobile) {
      dispatch(editorSlice.actions.bumpTextSidebarPulse())
    }
    if (selectGestureRef.current.exceeded) {
      return
    }
    pauseIfPlaying()
    if (isActive) {
      return
    }
    dispatch(
      editorThunks.selectSingleTimelineItem({
        id: item.id,
        type: item.type
      })
    )
  }, [item.id, item.type, isActive, isMobile, pauseIfPlaying])

  const {
    handleDrag,
    handleDragEnd,
    handleResizeStart: onResizeStart,
    handleResize,
    handleResizeEnd: onResizeEnd
  } = useMovableActions(item)

  const handleResizeStart = useCallback(
    (e: Parameters<typeof onResizeStart>[0]) => {
      markSelectGestureExceeded()
      isResizingRef.current = true
      setActiveResizeDir(directionVectorToKey(e.direction))
      e.setMin([MIN_RESIZE_SIZE, MIN_RESIZE_SIZE])

      // If it's a text item, compute and set max resize dimensions
      if (item.meta?.textData?.fontSize) {
        const maxScaling = MAX_FONT_SIZE / item.meta.textData.fontSize
        e.setMax([item.width * maxScaling, item.height * maxScaling])
      }
      onResizeStart(e)
    },
    [
      markSelectGestureExceeded,
      onResizeStart,
      setActiveResizeDir,
      directionVectorToKey,
      isResizingRef,
      item.width,
      item.height,
      item.meta?.textData?.fontSize
    ]
  )

  const handleResizeEnd = useCallback(
    (e: Parameters<typeof onResizeEnd>[0]) => {
      isResizingRef.current = false
      setActiveResizeDir(null)
      onResizeEnd(e)
    },
    [onResizeEnd, isResizingRef, setActiveResizeDir]
  )

  useEffect(() => {
    moveableRef.current?.updateRect()
  })

  useEffect(
    () => () => {
      selectGestureCleanupRef.current?.()
    },
    []
  )

  return (
    <>
      <Styled.ItemFrame
        ref={frameRef}
        className={CANVAS_ITEM_FRAME_CLASS_NAME}
        $left={item.coordinates.x}
        $top={item.coordinates.y}
        $width={displayWidth}
        $height={displayHeight}
        $zIndex={item.zIndex}
        $rotation={item.rotation}
        $flipX={item.flip?.x ?? false}
        $isActive={isActive}
        $isMovableVisible={isMovableVisible}
        $isSelectionChromeVisible={isSelectionChromeVisible}
        onPointerDown={startSelectGestureTracking}
        onClick={maybeHandleClick}
        onContextMenu={handleContextMenu}
      />

      {isSelectionChromeVisible && (
        <Moveable
          ref={assignMoveableRef}
          target={frameRef}
          zoom={canvasScale > 0 ? 1 / canvasScale : 1}
          origin={false}
          hideDefaultLines
          renderDirections={getRenderDirections()}
          draggable
          onDragStart={markSelectGestureExceeded}
          onDrag={handleDrag}
          throttleDrag={0}
          onDragEnd={handleDragEnd}
          keepRatio
          resizable
          throttleResize={0}
          onResizeStart={handleResizeStart}
          onResize={handleResize}
          onResizeEnd={handleResizeEnd}
          {...snapProps}
          onClick={({ isTarget }) => {
            if (isTarget) maybeHandleClick()
          }}
        />
      )}
    </>
  )
}

export default CanvasItemFrame
